import { useCallback, useEffect, useState } from "react";
import { DetailLolCharModel } from "../models/detail_lol_character";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Translate } from "../components/translate";
import { useLanguage } from "../stores/language_store";

export function DetailLolCharacter() {
  const [detailChar, setDetailChar] = useState<DetailLolCharModel>();
  const params = useParams();
  const navigate = useNavigate();
  const language = useLanguage();

  const fetchDetailChar = useCallback(
    (id: string) => {
      let lang = "fr_FR";
      if (language === "english") {
        lang = "en_US";
      }
      fetch(
        `https://ddragon.leagueoflegends.com/cdn/15.2.1/data/${lang}/champion/${id}.json`
      )
        .then((response) => response.json())
        .then((raw) => {
          setDetailChar(raw.data[id]);
        })
        .catch(() => navigate("/404"));
    },
    [navigate, language]
  );

  useEffect(() => {
    if (params.id !== undefined) {
      fetchDetailChar(params.id);
    }
  }, [fetchDetailChar, params.id]);

  const backgroundUrl = detailChar?.id
    ? `https://ddragon.leagueoflegends.com/cdn/img/champion/splash/${detailChar.id}_0.jpg`
    : "";

  return (
    <GlobalContainer background={backgroundUrl}>
      <BackButton>
        <LinkButton onClick={() => navigate(-1)}>
          <Translate value="return" />
        </LinkButton>
      </BackButton>
      {detailChar === undefined ? (
        <div>Loading...</div>
      ) : (
        <Container>
          <Title>{detailChar.title.toUpperCase()}</Title>
          <TitleName>{detailChar.name.toUpperCase()}</TitleName>
          <ContainerDescription>
            <CharDescription>{detailChar.lore}</CharDescription>
            <TitleSpellList>
              <Translate value="role" />
            </TitleSpellList>
            <ContainerRole>
              {detailChar.tags.map((tag) => (
                <div key={tag}>
                  <Translate value={`lol_tag_${tag.toLocaleLowerCase()}`} />
                </div>
              ))}
            </ContainerRole>
          </ContainerDescription>
          <ContainerSpells>
            <TitleSpellList>
              <Translate value="spells_list" />
            </TitleSpellList>
            <SpellsRow>
              {detailChar.spells.map((spell) => (
                <Spell key={spell.name}>
                  <SpellImage
                    alt={spell.name}
                    src={`https://ddragon.leagueoflegends.com/cdn/15.2.1/img/spell/${spell.image.full}`}
                  />
                  <SpellName>{spell.name}</SpellName>
                </Spell>
              ))}
            </SpellsRow>
            <ContainerPassive>
              <TitleSpellList>
                <Translate value="passive" />
              </TitleSpellList>
              <SpellName>{detailChar.passive.name}</SpellName>
              <SpellDescription>
                {detailChar.passive.description.replaceAll("<br>", " ")}
              </SpellDescription>
            </ContainerPassive>
          </ContainerSpells>
        </Container>
      )}
    </GlobalContainer>
  );
}
const GlobalContainer = styled.div<{ background: string }>`
  background-image: url(${(props) => props.background});
  background-size: cover;
  background-position: center;
  min-height: 100vh; /* S'assure que la page couvre au moins l'écran */
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const Title = styled.div`
  color: #886829;
  font-weight: bold;
  font-size: 30px;
  font-style: italic;
`;

const TitleName = styled.div`
  font-size: 50px;
  font-weight: bold;
  padding: 10px;
  color: white;
`;

const ContainerDescription = styled.div`
  @media (max-width: 1024px) {
    width: auto;
    height: auto;
  }
  width: 50%;
  height: 20%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #886829;
  padding: 10px;
  margin-bottom: 10px;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
`;

const ContainerRole = styled.div`
  padding-top: 20px;
  display: flex;
  gap: 20px;
`;

const CharDescription = styled.div`
  padding-bottom: 20px;
`;

const ContainerSpells = styled.div`
  background-color: #0c0b38;
  padding: 50px;
  width: 100%;
`;

const TitleSpellList = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 10px;
  color: white;
  text-align: center;
`;

const Spell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SpellsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(80px, 1fr)
  ); /* S'adapte dynamiquement */
  gap: 15px;
  justify-content: center;
  align-items: baseline;
  text-align: center;
`;

const SpellImage = styled.img`
  width: 64px;
  height: 64px;
`;

const SpellName = styled.div`
  margin-top: 5px;
  font-size: 16px;
  text-align: center;
  color: #256672;
  padding-bottom: 10px;
`;

const SpellDescription = styled.div`
  color: white;
`;

const LinkButton = styled.button`
  cursor: pointer;
  display: inline-block;
  text-decoration: none;
  background-color: rgb(56, 113, 219);
  border-radius: 5px;
  padding: 10px 20px;
  border: 1px solid transparent;
  color: white;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgb(36, 93, 199);
    transform: scale(1.05);
  }
`;
const BackButton = styled.div`
  padding: 20px;
`;

const ContainerPassive = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 30px;
  align-items: center;
`;
